import React, { Component } from 'react';
import TwoColumnTable from '../TwoColumnTable';
import ContentOutline from '../ContentOutline';
import TextBlock from '../TextBlock';

import sharedStyles from '../sharedStyles.scss';

const EXAM_SUMMARY = [
  {
    topic: 'Seeks Business for the Broker-Dealer from Customers and Potential Customers',
    description:
      'The basics of customer-facing sales activities related to a general securities representative role. What you need to know and do when describing investment products and services to customers with the intent of obtaining their business.',
    weight: '9 questions, 7%',
  },
  {
    topic:
      'Opens Accounts after Obtaining and Evaluating Customers’ Financial Profile and Investment Objectives',
    description:
      'The rules and processes you must follow when opening a customer account and informing them of their options. Also includes the requirements to make reasonable efforts to obtain customer profile information and report anything suspicious.',
    weight: '11 questions, 9%',
  },
  {
    topic:
      'Provides Customers with Information about Investments, Makes Suitable Recommendations, Transfers Assets and Maintains Appropriate Records',
    description:
      'The heart of the exam. Covers how you must provide customers with information about their investment options, especially the suitability of investments for them given their profile and goals. This requires being fluent in the risks, benefits, and typical investors of major financial products.',
    weight: '91 questions, 73%',
  },
  {
    topic:
      'Obtains and Verifies Customers’ Purchase and Sales Instructions and Agreements; Processes, Completes, and Confirms Transactions',
    description:
      'The logistics portion, covering how to obtain quotes, execute orders, and how to enlist your supervisor to assist in the resolution of discrepancies, disputes, and errors.',
    weight: '14 questions, 11%',
  },
];

class FinraS7 extends Component {
  render() {
    const { currentUser, product, inflatedContent } = this.props;

    return (
      <div className={sharedStyles.layout}>
        <div className={sharedStyles.title}>
          General Securities Representative Exam{' '}
          <span className={sharedStyles.shortTitle}>(FINRA Series 7)</span>
        </div>
        <div className={sharedStyles.explanation}>
          <div className={sharedStyles.blurb}>
            The Series 7 exam — the General Securities Representative Qualification
            Examination (GS) — assesses the competency of an entry-level registered
            representative to perform their job as a general securities representative.
          </div>
          <div className={sharedStyles.facts}>
            <ul>
              <li>
                <em>Time</em> 3 hours and 45 minutes
              </li>
              <li>
                <em>Format</em> 125 multiple choice
              </li>
              <li>
                <em>Passing score</em> 72% (90/125)
              </li>
              <li>
                <em>Exam fee</em> $300
              </li>
              <li>
                <em>Details</em>{' '}
                <a
                  href="https://www.finra.org/registration-exams-ce/qualification-exams/series7"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  FINRA <i className="fad fa-external-link" />
                </a>
              </li>
            </ul>
          </div>
        </div>

        <TextBlock title="Overview">
          <p>
            Also referred to as the Series 7 &quot;Top-Off&quot; Exam. This exam must be
            passed in conjunction with the Securities Industry Essentials (SIE) Exam in
            order for the candidate to receive a full General Securities license from
            FINRA. You must be sponsored by a FINRA member firm in order to take the
            Series 7.
          </p>
        </TextBlock>

        <TwoColumnTable title="FINRA's exam summary" records={EXAM_SUMMARY} />

        <ContentOutline
          currentUser={currentUser}
          product={product}
          inflatedContent={inflatedContent}
        />

        <TextBlock title="Eligibility and sponsorship">
          <p>
            Candidates cannot take the FINRA Series 7 Exam or other representative-level
            qualification exams unless they are associated with and sponsored by a FINRA
            member firm or other applicable self-regulatory organization (SRO) member
            firm. For more information on registration requirements, refer to{' '}
            <a
              href="https://www.finra.org/rules-guidance/rulebooks/finra-rules/1210"
              target="_blank"
              rel="noopener noreferrer"
            >
              FINRA Rule 1210
            </a>
            .
          </p>
          <p>
            The{' '}
            <a
              href="https://www.finra.org/registration-exams-ce/qualification-exams/securities-industry-essentials-exam-sie"
              target="_blank"
              rel="noopener noreferrer"
            >
              FINRA SIE Exam
            </a>{' '}
            is a corequisite to the Series 7 exam. Candidates must pass both the Series 7
            exam and the SIE exam to obtain the FINRA General Securities registration.
            Achievable offers a{' '}
            <a
              href="https://achievable.me/exams/finra-sie/overview/"
              target="_blank"
              rel="noopener noreferrer"
            >
              FINRA SIE Exam course
            </a>
            .
          </p>
          <p>
            For more information on the registration process, see FINRA’s{' '}
            <a
              href="https://www.finra.org/registration/individual-registration/register-new-candidate"
              target="_blank"
              rel="noopener noreferrer"
            >
              Register a New Candidate
            </a>{' '}
            help page.
          </p>
        </TextBlock>

        <TextBlock title="Administration of the exam">
          <p>
            The Series 7 exam is administered via computer. A tutorial on how to take the
            exam is provided prior to taking the exam.
          </p>
          <p>
            Each candidate’s exam includes 10 additional, unidentified pretest items that
            do not contribute toward the candidate&apos;s score. The pretest items are
            randomly distributed throughout the exam. Therefore, each candidate’s exam
            consists of a total of 135 items (125 scored and 10 unscored).
          </p>
          <p>
            There is no penalty for guessing. Therefore, candidates should attempt to
            answer all items.
          </p>
          <p>
            Candidates will be allowed 3 hours and 45 minutes to complete the Series 7
            exam.
          </p>
          <p>
            Candidates are not permitted to bring reference materials to their testing
            session. Severe penalties are imposed on candidates who cheat or attempt to
            cheat on FINRA-administered exams.
          </p>
        </TextBlock>

        <TextBlock title="Scoring of the exam">
          <p>
            All candidate test scores are placed on a common scale using a statistical
            adjustment process known as equating. Equating scores to a common scale
            accounts for the slight variations in difficulty that may exist among the
            different sets of exam items that candidates receive. This allows for a fair
            comparison of scores and ensures that every candidate is held to the same
            passing standard regardless of which set of exam items they received.
          </p>
        </TextBlock>
      </div>
    );
  }
}

export default FinraS7;
